<template>
    <div class="documents-add">
        <div class="drop-file">
            <DropArea :accept="acceptTypes"
                      :multiple="multiple"
                      class="drop-area"
                      @file="appendFile">

                <template #description>
                    <div v-if="singleFileCheck">{{ formatMaxFileSize }} • Максимальный размер файла</div>
                    <div v-if="!onlyOneFile">Общий размер не должен превышать {{ formatMaxTotalFilesSize }}</div>
                </template>
            </DropArea>
        </div>

        <div class="progress-bar">
            <div class="title">Загружено: {{ formatTotalFilesSize }} из {{ formatMaxTotalFilesSize }}</div>
            <ProgressBar :max="calcMaxTotalFilesSize" :value="totalFilesSize" class="progress" />
        </div>

        <button v-if="canAddGroup"
                class="no-border accent add-group iconed"
                @click="$emit('editGroup')">
            <IconAddGroup class="icon" />
            <span class="text">Создать папку</span>
        </button>

        <div v-if="!empty" class="attachment-list">
            <template v-for="(it, id) in model.groups">
                <AttachmentGroup :key="'g-' + id"
                                 :can-edit="canEdit"
                                 :can-remove="canRemove"
                                 :can-restore="canRestore"
                                 :removed="it.removed || false"
                                 :value="it"
                                 class="attachment"
                                 @edit="$emit('editGroup', it)"
                                 @remove="removeGroup(it, id)"
                                 @restore="restoreGroup(it)" />
            </template>

            <template v-for="(it, id) in model.documents">
                <AttachmentEdit v-if="it.state === DOCUMENT_STATE_EDIT"
                                :value="it"
                                :key="'ea-' + id + it.value.name"
                                class="attachment"
                                @remove="removeFile(it)"
                                @save="saveFile(it)" />

                <AttachmentInfo v-else
                                :key="'a-' + id"
                                :can-edit="canEdit"
                                :can-remove="canRemove"
                                :can-restore="canRestore"
                                :loading="it.loading"
                                :removed="it.removed || false"
                                :value="it.value"
                                class="attachment"
                                @edit="it.state = DOCUMENT_STATE_EDIT"
                                @remove="removeFile(it)"
                                @restore="restoreFile(it)" />
            </template>
        </div>

    </div>
</template>

<script>
import AttachmentEdit from '@/common/comp/attachment/AttachmentEdit.vue';
import AttachmentGroup from '@/common/comp/attachment/AttachmentGroup.vue';
import Document from '@/common/page_comp/Documents/document.js';

import { DOCUMENT_STATE_EDIT, DOCUMENT_STATE_NORMAL } from '@/common/page_comp/Documents/document.states.js';
import { documentGroupInvalidRules, documentsInvalidRules } from '@/common/page_comp/Documents/documents.util.js';
import { formatFileSize } from '@/common/page_comp/Documents/fileApi.js';
import { updateState } from '@/common/util/util.js';
import { documentTypeDict } from '@/restapi/document/document.type.js';
import { showNotice } from '@dsee/components/popups/message.js';
import { deepCopy, deepEquals } from '@dsee/util/objects.js';
import { useVuelidate } from '@vuelidate/core';
import { computed } from 'vue';

import IconAddGroup from '@/common/page_comp/Documents/icons/add_group.svg?component';


// 1МБ
const MB = 1024 * 1024;

function getFilesSize(files) {
    return files.reduce((total, it) => {
        return total + it.size;
    }, 0);
}

function anyHeavyFile(files, maxFileSize) {
    return files.some(it => it.size > maxFileSize);
}


export default {
    name: 'DocumentsAdd',
    components: {
        AttachmentEdit,
        AttachmentGroup,
        IconAddGroup,
    },
    setup() {
        return {
            v: useVuelidate({ $autoDirty: true }),
        };
    },
    props: {
        acceptTypes: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        documents: {
            type: Array,
            default: () => [],
        },
        groups: {
            type: Array,
            default: () => [],
        },
        canRemove: {
            type: Boolean,
            default: true,
        },
        canRestore: {
            type: Boolean,
            default: false,
        },
        canAddGroup: {
            type: Boolean,
            default: false,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        otherFilesSize: {
            type: Number,
            default: 0,
        },
        maxFileSize: {
            type: Number,
            default: 15,
        },
        maxTotalFilesSize: {
            type: Number,
            default: 100,
        },
        currentAccessType: {
            type: String,
            default: 'PUBLIC',
        },
        canUploadDocument: {
            type: Boolean,
            default: true,
        },
        calcSizeOnlyUploaded: {
            type: Boolean,
            default: false,
        },
        onlyOneFile: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            model: {
                documents: [],
                groups: [],
            },

            dicts: {
                documentTypeDict: [],
            },
        };
    },
    computed: {
        empty() {
            return this.model.documents.length === 0 && this.model.groups.length === 0;
        },
        editingDocuments() {
            return this.model.documents.filter(it => it.state === DOCUMENT_STATE_EDIT);
        },
        normalDocuments() {
            return this.model.documents.filter(it => it.state === DOCUMENT_STATE_NORMAL);
        },
        DOCUMENT_STATE_EDIT() {
            return DOCUMENT_STATE_EDIT;
        },
        calcMaxFileSize() {
            return this.maxFileSize * MB;
        },
        calcMaxTotalFilesSize() {
            return this.maxTotalFilesSize * MB;
        },
        singleFileCheck() {
            return this.maxFileSize !== 0;
        },
        totalFilesSize() {
            let totalSize = 0;

            for (const group of this.model.groups) {
                if (!group.removed) {
                    for (const doc of group.value.documents) {
                        const can = this.calcSizeOnlyUploaded ? (doc.id || doc.loading) && !doc.removed : !doc.removed;
                        if (can) {
                            totalSize += doc.value.size;
                        }
                    }
                }
            }
            for (const doc of this.model.documents) {
                const can = this.calcSizeOnlyUploaded ? (doc.id || doc.loading) && !doc.removed : !doc.removed;
                if (can) {
                    totalSize += doc.value.size;
                }
            }

            return totalSize + this.otherFilesSize;
        },
        formatTotalFilesSize() {
            return formatFileSize(this.totalFilesSize);
        },
        formatMaxTotalFilesSize() {
            return formatFileSize(this.calcMaxTotalFilesSize, 0);
        },
        formatMaxFileSize() {
            return formatFileSize(this.calcMaxFileSize, 0);
        },
    },
    provide() {
        return {
            documentTypeDict: computed(() => this.dicts.documentTypeDict),
        };
    },
    validations() {
        return {
            model: {
                documents: {
                    edit: documentsInvalidRules,
                },
                groups: {
                    edit: documentGroupInvalidRules,
                },
            },
        };
    },
    async created() {
        this.dicts.documentTypeDict = await documentTypeDict();
        this.updateModel();
    },
    methods: {
        updateModel() {
            const documents = this.documents;
            const groups = this.groups;

            updateState(this.model, { documents, groups });
        },
        appendFile({ accepted }) {
            if (!accepted.length)
                return;

            if (!this.canEdit) {
                const resp = this.checkFreeSpace(accepted);
                if (!resp)
                    return;
            }

            if (this.onlyOneFile) {
                this.model.documents = [];
            }

            const result = [];
            for (const file of accepted) {
                file.accessType = this.currentAccessType;
                const item = new Document(file);
                result.push(item);

                if (this.canEdit) {
                    item.state = DOCUMENT_STATE_EDIT;
                } else if (this.canUploadDocument) {
                    item.upload();
                }
            }

            this.model.documents = result.concat(this.model.documents);
        },
        saveFile(file) {
            const newFile = !file.id;

            if (newFile) {
                const resp = this.checkFreeSpace([ file.value ]);
                if (!resp)
                    return;

                file.upload();
            }

            file.state = DOCUMENT_STATE_NORMAL;
        },
        removeFile(file) {
            const newFile = !file.id;

            if (newFile) {
                const pos = this.model.documents.findIndex(it => deepEquals(it, file));
                if (pos === -1)
                    return;

                this.model.documents.splice(pos, 1);
            } else {
                file.removed = true;
            }
        },
        restoreFile(file) {
            const resp = this.checkFreeSpace([ file.value ]);
            if (!resp)
                return;

            file.removed = false;
        },
        removeGroup(group, pos) {
            const newFile = !group.id;

            if (newFile) {
                this.model.groups.splice(pos, 1);
            } else {
                group.removed = true;
            }
        },
        restoreGroup(group) {
            const resp = this.checkFreeSpace(group.value.documents.map(it => it.value));
            if (!resp)
                return;

            group.removed = false;
        },
        checkFreeSpace(files) {
            if (this.singleFileCheck && anyHeavyFile(files, this.calcMaxFileSize)) {
                showNotice({
                    title: 'Превышен лимит',
                    text: 'Превышены ограничения по добавлению документов. Размер одного документа не должен превышать '
                        + this.formatMaxFileSize,
                });
                return false;
            }

            const newFilesSize = getFilesSize(files);

            if ((this.totalFilesSize + newFilesSize) > this.calcMaxTotalFilesSize) {
                showNotice({
                    title: 'Превышен лимит',
                    text: 'Превышены ограничения по добавлению документов. Размер всех документов не должен превышать '
                        + this.formatMaxTotalFilesSize,
                });

                return false;
            }

            return true;
        },
    },
    watch: {
        documents: {
            immediate: true,
            handler() {
                this.updateModel();
            },
        },
        groups: {
            immediate: true,
            handler() {
                this.updateModel();
            },
        },
        'model.documents': {
            immediate: true,
            handler() {
                this.$emit('update:documents', this.model.documents);
            },
        },
        'model.groups': {
            immediate: true,
            handler() {
                this.$emit('update:groups', this.model.groups);
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.documents-add {
    .drop-file {
        margin-bottom: 20px;
    }

    .progress-bar {
        margin-bottom: 20px;

        .title {
            font-size: 12px;
            line-height: 16px;
            margin-bottom: 4px;
        }

        .progress {
            background: rgba(249, 249, 251, 1);
            height: 12px;
            border-radius: 12px;
        }
    }

    .add-group {
        margin-bottom: 20px;
    }

    .attachment-list {
        .attachment {
            margin-bottom: 20px;
        }
    }
}
</style>
